import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Core from "class/Core";
import config, { getBasicAuthHeaders } from "utils/configAdapter";
import { Card, Form, Row, Col, Select, Button, Table, Typography, Space, Spin, message, Input } from "antd";
import * as Icons from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useMutation, useQuery } from "react-query";

dayjs.locale("pt-br");

const getIntegrarUrl = () => {
    const hostname = window.location.hostname;
    const env = hostname === 'localhost' ? 'local'
        : hostname.includes('preprod') ? 'development'
            : 'production';
    return config[env].url;
};

export const getCurrentReferenceMonth = () => dayjs().format('MM-YYYY');

export const isSearchFormValid = (values = {}) => !!(
    values.favorecido &&
    (values.cpf?.trim() || values.nome?.trim())
);

const TrabalhadorPrestadores = (props) => {
    const { Title, Text } = Typography;
    const [form] = Form.useForm();
    const currentReferenceMonth = getCurrentReferenceMonth();

    // Instanciar Core
    const core = new Core();

    // States
    const [tableData, setTableData] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [qtdTotal, setQtdTotal] = useState(0);

    // Verificar autenticação ao montar componente
    useEffect(() => {
        core.isAuth();
    }, []);

    // Query para buscar favorecidos
    const { data: queryFavorecidos = { list: [], beneficioId: null }, isLoading: loadingFavorecidos } = useQuery(
        "FAVORECIDOS-TRABALHADORES",
        async () => {
            const response = await fetch(
                `${core.getUrl()}/api-v1/empresa/relatorios/relacao-assistencia`,
                {
                    method: "GET",
                    headers: core.getHeadersFetch(true),
                }
            );

            const result = await core.responseConvert(response);

            return {
                list: result.content.map((el) => ({
                    value: el.enti_cd_nseq,
                    label: el.enti_ds_sigla,
                })),
                // Quando beneficio vier populado, usa seu primeiro valor como assistencia na busca
                beneficioId: result.assistencia ? result.assistencia : null,
            };
        },
        {
            onError: (err) => {
                core.catchFecth(err);
            }
        }
    );

    const favorecidos = queryFavorecidos.list;
    const beneficioId = queryFavorecidos.beneficioId;

    // Mutation para buscar dados
    const { mutate: executeBusca, isLoading: loadingBusca } = useMutation(
        async (params) => {
            const queryParams = new URLSearchParams({
                mes: params.mes,
                excel: params.excel,
                ...(params.beneficioId ? { assistencia: params.beneficioId } : { favorecido: params.favorecido }),
                ...(params.cpf ? { cpf: params.cpf } : {}),
                ...(params.nome ? { nome: params.nome } : {}),
            });

            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/excel-trabalhador-assistencia?${queryParams}`,
                {
                    method: 'GET',
                    headers: getBasicAuthHeaders(),
                }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        },
        {
            onSuccess: (result) => {
                const dados = result.content || [];
                setTableData(dados);
                setShowTable(true);

                setQtdTotal(result.resumo?.total_trabalhadores_mes || 0);

                message.success(`${dados.length} registros encontrados`);
            },
            onError: (err) => {
                core.catchFecth(err);
            }
        }
    );

    const onFinish = (values) => {
        if (!values.favorecido) {
            message.error('Favorecido não selecionado');
            return;
        }

        const params = {
            mes: currentReferenceMonth,
            favorecido: values.favorecido,
            excel: "nao",
            beneficioId,
            cpf: values.cpf?.trim() || undefined,
            nome: values.nome?.trim() || undefined,
        };

        executeBusca(params);
    };

    // Handler para download Excel via blob (Basic Auth não suporta window.location.href)
    const onDownloadExcel = async () => {
        const values = form.getFieldsValue();
        const params = new URLSearchParams({
            mes: currentReferenceMonth,
            excel: 'sim',
            acordos: 'true',
            ...(beneficioId ? { assistencia: beneficioId } : { favorecido: values.favorecido }),
        });

        try {
            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/excel-trabalhador-assistencia?${params}`,
                { method: 'GET', headers: getBasicAuthHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const blob = await response.blob();
            if (blob.size === 0) throw new Error('Arquivo Excel vazio recebido');

            const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
            const filenameMatch = contentDisposition && contentDisposition.match(/filename\s*=\s*"?(.*?)"?$/i);
            const filename = (filenameMatch && filenameMatch[1]) || `trabalhadores_${currentReferenceMonth}.xlsx`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 100);

            message.success('Excel gerado com sucesso!');
        } catch (err) {
            message.error('Erro ao gerar Excel');
        }
    };

    // Colunas da tabela
    const columns = [
        { title: "Nome", dataIndex: "nome", key: "nome", width: 250, ellipsis: true, fixed: "left" },
        { title: "CPF", dataIndex: "cpf", key: "cpf", width: 150, fixed: "left" },
        { title: "Nascimento", dataIndex: "dt_nascimento", key: "dt_nascimento", width: 120 },
        { title: "Sexo", dataIndex: "sexo", key: "sexo", width: 80 },
        { title: "Admissão", dataIndex: "dt_admissao", key: "dt_admissao", width: 120 },
        { title: "Inclusão", dataIndex: "dt_inclusao", key: "dt_inclusao", width: 120 },
        { title: "Empresa", dataIndex: "razao_social", key: "razao_social", width: 250, ellipsis: true },
        { title: "CNPJ", dataIndex: "cnpj", key: "cnpj", width: 140 },
        { title: "Plano", dataIndex: "placa", key: "placa", width: 80 },
        {
            title: "Dt Vcto",
            dataIndex: "data_vencimento",
            key: "data_vencimento",
            width: 120,
            render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-"
        },
        {
            title: "Dt Pgto",
            dataIndex: "data_pagamento",
            key: "data_pagamento",
            width: 120,
            render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-"
        },
        { title: "N.Numero", dataIndex: "nosso_numero", key: "nosso_numero", width: 120 },
        { title: "Situação", dataIndex: "situacao", key: "situacao", width: 100 },
        { title: "Acordo", dataIndex: "acordo", key: "acordo", width: 90 },
    ];

    const loading = loadingFavorecidos || loadingBusca;

    return (
        <>
            {/* Header */}
            <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: "20px 24px" }}>
                <Row align="middle" justify="space-between">
                    <Col>
                        <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
                            <Icons.UserOutlined style={{ marginRight: 8 }} />
                            Relação de Trabalhador - Pagamento
                        </Title>
                        <Text type="secondary" style={{ fontSize: "14px" }}>
                            Consulte os trabalhadores com direito no mês atual.
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* Filtros */}
            <Card
                title={
                    <Space>
                        <Icons.SearchOutlined />
                        <span>Filtros de Pesquisa</span>
                    </Space>
                }
                style={{ marginBottom: 24 }}
                bodyStyle={{ padding: "20px 24px" }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={(_, allValues) => {
                        setFormValid(isSearchFormValid(allValues));
                    }}
                >
                    <Row gutter={[16, 16]}>
                        {/* alteração chamado BSF-3139 */}
                        {/* <Col xs={24} sm={12} md={8} lg={8}>
                            <Form.Item
                                name="mes"
                                label={
                                    <Space>
                                        <Icons.CalendarOutlined />
                                        <span>Mês de Referência</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: "Selecione o mês" }]}
                            >
                                <DatePicker
                                    picker="month"
                                    locale={ptBR}
                                    format="MM/YYYY"
                                    placeholder="Selecione o mês"
                                    style={{ width: "100%" }}
                                    size="large"
                                    disabledDate={(current) => current && current >= dayjs().startOf('month')}
                                />
                            </Form.Item>
                        </Col> */}

                        <Col xs={24} sm={12} md={8} lg={8}>
                            <Form.Item
                                name="favorecido"
                                label={
                                    <Space>
                                        <Icons.BankOutlined />
                                        <span>Favorecido</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: "Selecione o favorecido" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Selecione o favorecido"
                                    options={favorecidos}
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    size="large"
                                    loading={loadingFavorecidos}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={8}>
                            <Form.Item
                                name="cpf"
                                label={
                                    <Space>
                                        <Icons.IdcardOutlined />
                                        <span>CPF</span>
                                    </Space>
                                }
                            >
                                <Input placeholder="Digite o CPF" size="large" allowClear />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={8}>
                            <Form.Item
                                name="nome"
                                label={
                                    <Space>
                                        <Icons.SearchOutlined />
                                        <span>Nome</span>
                                    </Space>
                                }
                            >
                                <Input placeholder="Digite o nome" size="large" allowClear />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={8}>
                            <Form.Item label=" " colon={false}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    disabled={!formValid || loading}
                                    loading={loading}
                                    icon={<Icons.SearchOutlined />}
                                    style={{ width: "100%" }}
                                >
                                    BUSCAR
                                </Button>
                                {/* <Button
                                    size="large"
                                    disabled={!showTable || loading}
                                    icon={<Icons.FileExcelOutlined />}
                                    style={{ width: "100%" }}
                                    onClick={onDownloadExcel}
                                >
                                    EXCEL
                                </Button> */}
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Total */}
            {showTable && (
                <Card style={{ marginBottom: 16 }}>
                    <Text strong>Total de Trabalhadores: </Text>
                    <Text style={{ fontSize: 18, color: "#1890ff" }}>{qtdTotal.toLocaleString("de-DE")}</Text>
                </Card>
            )}

            {/* Tabela */}
            {showTable && (
                <Card>
                    <Spin spinning={loading}>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            rowKey={(record, index) => `row-${index}`}
                            pagination={{
                                pageSize: 50,
                                hideOnSinglePage: true,
                                showSizeChanger: false,
                            }}
                            scroll={{ x: 1400 }}
                            locale={{
                                emptyText: (
                                    <div style={{ textAlign: "center" }}>
                                        <b>Informe os parâmetros necessários para a pesquisa.</b>
                                    </div>
                                ),
                            }}
                        />
                    </Spin>
                </Card>
            )}

            {/* Empty state */}
            {!showTable && !loading && (
                <Card style={{ textAlign: "center", padding: "40px 20px" }}>
                    <div style={{ color: "#999" }}>
                        <Icons.SearchOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
                        <Title level={4} type="secondary">
                            Configure os filtros e clique em "BUSCAR"
                        </Title>
                        <Text type="secondary">
                            Informe os filtros e o favorecido para visualizar os trabalhadores
                        </Text>
                    </div>
                </Card>
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.user,
    };
};

export default connect(mapStateToProps)(TrabalhadorPrestadores);
